import React, { useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

// Render locally so PDF visibility does not depend on an embedded browser plugin.
export default function InlineCvPreview({ url }) {
  const { language } = useLanguage();
  const ar = language === 'ar';
  const copy = (en, arabic) => ar ? arabic : en;
  const [document, setDocument] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const canvas = useRef(null);
  const viewport = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let task;
    setDocument(null); setPageNumber(1); setStatus('loading'); setZoomed(false);
    import('pdfjs-dist/webpack.mjs').then(({ getDocument }) => {
      if (cancelled) return null;
      task = getDocument({ url, isEvalSupported: false, useSystemFonts: true });
      return task.promise;
    }).then((pdf) => { if (!cancelled && pdf) setDocument(pdf); })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; task?.destroy()?.catch(() => {}); };
  }, [url, attempt]);

  useEffect(() => {
    if (!document) return undefined;
    let cancelled = false;
    let renderTask;
    const element = canvas.current;
    setStatus('loading');
    document.getPage(pageNumber).then((page) => {
      if (cancelled) return null;
      const normal = page.getViewport({ scale: 1 });
      // Render at high resolution, then let CSS fit the page to the available width.
      const scale = Math.min(window.devicePixelRatio || 1, 2) * 1.5;
      const size = page.getViewport({ scale });
      element.width = size.width;
      element.height = size.height;
      element.style.aspectRatio = `${normal.width} / ${normal.height}`;
      renderTask = page.render({ canvasContext: element.getContext('2d'), viewport: size });
      return renderTask.promise;
    }).then(() => {
      if (!cancelled) { setStatus('ready'); if (viewport.current) viewport.current.scrollTop = 0; }
    }).catch((error) => { if (!cancelled && error.name !== 'RenderingCancelledException') setStatus('error'); });
    return () => { cancelled = true; renderTask?.cancel(); };
  }, [document, pageNumber]);

  const number = value => new Intl.NumberFormat(language).format(value);
  return <div className={`js-profile-cv__preview ${zoomed ? 'is-zoomed' : ''}`}>
    <div className="js-profile-cv__pages">
      <span>{document ? copy(`Page ${number(pageNumber)} of ${number(document.numPages)}`, `الصفحة ${number(pageNumber)} من ${number(document.numPages)}`) : copy('CV preview', 'معاينة السيرة الذاتية')}</span>
      {document && <div>
        <button type="button" className="js-mobile-only js-profile-cv__zoom" aria-label={zoomed ? copy('Fit page', 'ملاءمة الصفحة') : copy('Enlarge CV', 'تكبير السيرة الذاتية')} title={zoomed ? copy('Fit page', 'ملاءمة الصفحة') : copy('Enlarge CV', 'تكبير السيرة الذاتية')} aria-pressed={zoomed} onClick={() => setZoomed(value => !value)}>
          {zoomed ? <FiZoomOut aria-hidden="true" /> : <FiZoomIn aria-hidden="true" />}
        </button>
        {document.numPages > 1 && <>
        <button type="button" aria-label={copy('Previous CV page', 'الصفحة السابقة من السيرة الذاتية')} disabled={pageNumber <= 1 || status === 'loading'} onClick={() => setPageNumber(value => value - 1)}>{ar ? <FiChevronRight /> : <FiChevronLeft />}</button>
        <button type="button" aria-label={copy('Next CV page', 'الصفحة التالية من السيرة الذاتية')} disabled={pageNumber >= document.numPages || status === 'loading'} onClick={() => setPageNumber(value => value + 1)}>{ar ? <FiChevronLeft /> : <FiChevronRight />}</button>
        </>}
      </div>}
    </div>
    <div className="js-profile-cv__viewport" ref={viewport} aria-busy={status === 'loading'}>
      {status === 'loading' && <p role="status" className="js-profile-cv__loading">{copy('Loading your CV…', 'جارٍ تحميل سيرتك الذاتية…')}</p>}
      {status === 'error' && <div className="js-profile-cv__fallback"><p role="alert">{copy('Could not display the CV preview.', 'تعذّر عرض معاينة السيرة الذاتية.')}</p><button type="button" onClick={() => setAttempt(value => value + 1)}>{copy('Try again', 'حاول مجدداً')}</button><a href={url} target="_blank" rel="noopener noreferrer">{copy('Open CV', 'افتح السيرة الذاتية')}</a></div>}
      <canvas ref={canvas} role="img" aria-label={copy(`CV preview, page ${number(pageNumber)}`, `معاينة السيرة الذاتية، الصفحة ${number(pageNumber)}`)} hidden={status !== 'ready'} />
    </div>
  </div>;
}
